import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Sun, Moon, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'sonner';
import { authAPI } from '../../services/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.requestPasswordReset(email);
      setSubmitted(true);
      // Clear email field
      setEmail('');
      // Redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } catch (error) {
      // Show generic message (don't reveal if email exists)
      toast.success('If an account exists with that email, you will receive a password reset link.');
      setSubmitted(true);
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
          <img
            src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwc2VjdXJpdHklMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcwOTQ4Mjc1fDA&ixlib=rb-4.1.0&q=85"
            alt="Data Security"
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="relative z-10 flex flex-col justify-between p-12 text-white">
            <div className="flex items-center gap-3">
              <img
                src="/fulllogo_transparent.png"
                alt="Assura"
                className="h-10 brightness-0 invert"
              />
            </div>
            <div>
              <h1 className="text-4xl font-bold mb-4">Compliance Without Complexity</h1>
              <p className="text-lg text-slate-300">
                Simplify compliance, manage risk, and protect data across your organization with our comprehensive Caribbean data protection compliance platform.
              </p>
            </div>
            <p className="text-sm text-slate-400">
              © 2026 Assura. All rights reserved.
            </p>
          </div>
        </div>

        {/* Right Panel - Success Message */}
        <div className="flex-1 flex items-center justify-center p-8 bg-background">
          <div className="absolute top-4 right-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>

          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <CardTitle className="text-2xl font-bold">Check your email</CardTitle>
              <CardDescription>
                If an account exists with that email, you will receive a password reset link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  The reset link will expire in 30 minutes. If you don't receive an email, check your spam folder.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full"
                  variant="outline"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwc2VjdXJpdHklMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcwOTQ4Mjc1fDA&ixlib=rb-4.1.0&q=85"
          alt="Data Security"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/fulllogo_transparent.png"
              alt="Assura"
              className="h-10 brightness-0 invert"
            />
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-4">Compliance Without Complexity</h1>
            <p className="text-lg text-slate-300">
              Simplify compliance, manage risk, and protect data across your organization with our comprehensive Caribbean data protection compliance platform.
            </p>
          </div>
          <p className="text-sm text-slate-400">
            © 2026 Assura. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <img
                src="/fulllogo_transparent.png"
                alt="Assura"
                className="h-8 dark:brightness-0 dark:invert"
              />
            </div>
            <CardTitle className="text-2xl font-bold">Reset your password</CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit-btn">
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>

              <div className="mt-4 text-center text-sm">
                <Link to="/login" className="text-blue-600 hover:underline flex items-center justify-center gap-1" data-testid="back-to-login-link">
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
